import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import { useCallback, useEffect, useMemo } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import { GridLogicOperator } from "@mui/x-data-grid-pro"

import { KnowledgeBaseListStore } from "./store"

import { ListPage } from "src/components/ListPage"
import { DataGridProTable } from "src/components/Table/DataGridPro"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import {
    getFilterModelForRepository,
    getSortModelForRepository,
} from "src/lib/data-grid-pro"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import {
    FilterModel,
    IAdvancedOperations,
    SortModel,
} from "src/types/data-grid-pro"
import { ConfirmModal } from "src/modals/confirm"
import { Repository } from "src/types/channel"
import { useInitializer } from "src/lib/initializer"
import { IPageFilterProps } from "src/components/PageFilter"
import { SegmentPickerButton } from "src/components/SegmentPickerButton"
import { removeLastIdSegment } from "src/helpers/removeLastIdSegment"
import { reportError } from "src/lib/report"
import { IKnowledgeItem } from "src/views/knowledge-base/pages/list/types"
import { useKnowledgeBaseListColumns } from "src/views/knowledge-base/pages/list/hooks/useKnowledgeBaseListColumns"
import { EModalMode } from "src/types/modal/modal"
import { KnowledgeItemModal } from "src/modals/knowledge-items"
import { LibraryInformationItemModal } from "src/modals/library-information-items"

const repository: Repository = "knowledge-base"

const View = observer(() => {
    const store = useStore(KnowledgeBaseListStore)
    const globalStore = useStore(GlobalStore)

    const { id } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const parentPath = removeLastIdSegment(location.pathname)

    // Check if we're in Library mode (from navigation state)
    const locationState = location.state as {
        knowledgeBaseCard?: string
        knowledgeBaseItemType?: string
    } | null
    const isLibraryMode = locationState?.knowledgeBaseCard === "Library"
    const forcedKnowledgeItemType = locationState?.knowledgeBaseItemType

    const columns = useKnowledgeBaseListColumns(
        store.agentAccessTypes,
        isLibraryMode,
    )

    const advancedOperationsModel: IAdvancedOperations = useMemo(
        () => ({
            pagination: "server",
            filtering: "server",
            sorting: "server",
        }),
        [],
    )

    const advanceQuery = useMemo(() => {
        let filter = getFilterModelForRepository(
            repository,
            globalStore.session.dataGridFilterModel,
        )

        // In Library mode, add hidden filter for information_item type
        if (isLibraryMode && Boolean(forcedKnowledgeItemType)) {
            const typeFilter = {
                field: "knowledge_item_type",
                operator: "equals",
                value: forcedKnowledgeItemType,
            }

            if (filter?.items != null) {
                filter = {
                    ...filter,
                    items: [...filter.items, typeFilter],
                }
            } else {
                filter = {
                    items: [typeFilter],
                    logicOperator: GridLogicOperator.And,
                }
            }
        }

        const sort = getSortModelForRepository(
            repository,
            globalStore.session.dataGridSortModel,
        )
        return { sort, filter }
    }, [
        globalStore.session.dataGridFilterModel,
        globalStore.session.dataGridSortModel,
        isLibraryMode,
        forcedKnowledgeItemType,
    ])

    const initialized = useInitializer(async () => {
        await store.init(
            globalStore.session.accessGroup.id ?? DEFAULT_ACCESS_GROUP.id,
            advanceQuery,
        )
    }, [globalStore.session.accessGroup])

    // Open the modal for a particular feature from url
    useInitializer(async () => {
        if (id !== undefined && !isNaN(Number(id))) {
            // Find the Knowledge Item in the loaded data
            const knowledgeItem = store.knowledgeItems.items.find(
                (item) => item.id === Number(id),
            )

            if (
                knowledgeItem?.source_item_type === "information_item" &&
                typeof knowledgeItem.source_item_id === "number"
            ) {
                // Open Information Item modal for shadow items
                await globalStore.modals.open(
                    () => (
                        <LibraryInformationItemModal
                            itemId={knowledgeItem.source_item_id}
                        />
                    ),
                    {
                        onClose: () => navigate(parentPath),
                    },
                )
            } else {
                // Open Knowledge Item modal for regular items
                await globalStore.modals.open(
                    () => (
                        <KnowledgeItemModal
                            id={Number(id)}
                            mode={EModalMode.Edit}
                        />
                    ),
                    {
                        onClose: () => navigate(parentPath),
                    },
                )
            }
        }
    }, [
        id,
        store.knowledgeItems.items,
        globalStore.modals,
        navigate,
        parentPath,
    ])

    useEffect(() => {
        return () => {
            store.dispose()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleNewFeatureClick = useCallback(() => {
        globalStore.modals.open(() => <KnowledgeItemModal />)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleCopyTemplateFeatureClick = useCallback(
        (item) => {
            if (item != null) {
                globalStore.modals.open(() => (
                    <KnowledgeItemModal id={item.id} mode={EModalMode.Copy} />
                ))
            } else {
                reportError(t`global.copy-feature-error`)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const header = useMemo(
        () => ({
            header: t`knowledge-base.header`,
            breadcrumbs: [
                t`knowledge-base.header`,
                t`knowledge-base.card.knowledge-card`,
            ],
            createOptions: {
                item: t`knowledge-base.create-new`,
                onClick: handleNewFeatureClick,
            },
        }),
        [handleNewFeatureClick],
    )

    const handleSegmentChange = useCallback(
        (segments: number[]) => store.loadSegments(segments, advanceQuery),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [advanceQuery],
    )

    const filter = useMemo(
        (): IPageFilterProps => ({
            actions: (
                <SegmentPickerButton
                    value={store.segments}
                    onChange={handleSegmentChange}
                />
            ),
        }),
        [store.segments, handleSegmentChange],
    )

    const handleFilterChange = useCallback(
        async (model: FilterModel) => {
            const sort = getSortModelForRepository(
                repository,
                globalStore.session.dataGridSortModel,
            )
            await store.query({ sort, filter: model })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [globalStore.session.dataGridSortModel],
    )

    const handleSortChange = useCallback(
        async (model: SortModel) => {
            const filter = getFilterModelForRepository(
                repository,
                globalStore.session.dataGridFilterModel,
            )
            await store.query({ filter, sort: model })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [globalStore.session.dataGridFilterModel],
    )

    const handleEditItem = useCallback(
        (item: IKnowledgeItem) => {
            // Check if this is a shadow item from an Information Item
            if (
                item.knowledge_item_type === "information_item" &&
                typeof item.source_item_id === "number"
            ) {
                // Open Information Item modal instead - this will load the original Information Item
                globalStore.modals.open(() => (
                    <LibraryInformationItemModal itemId={item.source_item_id} />
                ))
            } else {
                // Open Knowledge Item modal for regular items
                globalStore.modals.open(() => (
                    <KnowledgeItemModal id={item.id} />
                ))
            }
        },
        [globalStore.modals],
    )

    const handleDeleteItem = useCallback(
        (item: IKnowledgeItem) => {
            globalStore.modals.open(
                () => (
                    <ConfirmModal
                        onConfirm={async (confirmed) => {
                            if (confirmed) {
                                await store.deleteItem(item.id)
                            }
                        }}
                        title={t`knowledge-base-list-view.are-you-sure`}
                        content={`${t`knowledge-base-list-view.confirm-delete-item`} ${
                            item.title
                        }`}
                    />
                ),
                {
                    variant: "slide-up-w600",
                },
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const rowActionsRenderer = useCallback(
        (item: IKnowledgeItem) => [
            {
                text: t`knowledge-base-list-view.edit-item`,
                onClick: () => handleEditItem(item),
            },
            {
                text: t`knowledge-base-list-view.copy-template-feature`,
                onClick: () => handleCopyTemplateFeatureClick(item),
            },
            {
                text: t`knowledge-base-list-view.delete-item`,
                onClick: () => handleDeleteItem(item),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    return (
        <ListPage header={header} filter={filter} loading={!initialized}>
            <DataGridProTable
                paginator={store.knowledgeItems}
                data={store.knowledgeItems.items}
                columns={columns}
                advancedOperations={advancedOperationsModel}
                onRowClickEvent={(params) => handleEditItem(params.row)}
                rowActionsRenderer={rowActionsRenderer}
                repository={repository}
                loading={!initialized}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
            />
        </ListPage>
    )
})

export const KnowledgeBaseListView = () => (
    <StoreProvider Store={KnowledgeBaseListStore}>
        <View />
    </StoreProvider>
)
